import { useMemo } from 'react';
import { useBoardViews, useUpdateBoardView } from '@/features/boards/public';
import { BOARD_VIEW_TYPE } from '../types';
import type { TaskColumnId } from '../types';
import {
  parseListViewConfig,
  serializeListViewConfig,
  type ListViewSharedConfig,
} from '../utils/list-view-config';

/**
 * The List view's visible columns/order/width are a board-level setting shared by every member
 * (see the board-view-config backend requirement) — not a personal preference, unlike
 * `useTaskListConfig`'s `groupBy`/`sortBy`. Reads/writes the `config` blob of the board's
 * List-type `BoardViewDto`.
 */
export function useSharedListViewConfig(boardId: string) {
  const { views, isLoading } = useBoardViews(boardId);
  const listView = views.find((v) => v.type === BOARD_VIEW_TYPE.List);
  const updateView = useUpdateBoardView(boardId);

  const config = useMemo(() => parseListViewConfig(listView?.config), [listView?.config]);

  function persist(next: ListViewSharedConfig) {
    if (!listView?.id) return;
    updateView.mutate({ viewId: listView.id, payload: { config: serializeListViewConfig(next) } });
  }

  return {
    visibleColumns: config.visibleColumns,
    columnWidths: config.columnWidths,
    isLoading,
    setVisibleColumns: (visibleColumns: TaskColumnId[]) => persist({ ...config, visibleColumns }),
    setColumnWidths: (columnWidths: Record<string, number>) => persist({ ...config, columnWidths }),
  };
}
